import axios from "axios";

/**
 * Prenotazioni crisantemi — modello nuovo.
 *
 * Differenze rispetto al modulo `crisantemi` (che resta in piedi finché la
 * nuova UI non raggiunge la parità):
 *
 *  - la prenotazione è indirizzata per id, non per anno + p.iva
 *  - le quantità non partono una chiamata per cella: vengono accumulate in un
 *    buffer e inviate insieme, con un solo save lato server
 *  - lo stato locale è aggiornato in modo ottimistico, così la griglia risponde
 *    subito; la risposta del server sovrascrive e le celle toccate nel
 *    frattempo vengono riapplicate sopra
 *  - l'unità prenotata è la varietà (`id_varieta`); i colori embedded nel
 *    prodotto restano leggibili come storico (`id_colore`)
 */

const BASE = process.env.VUE_APP_API_ROOT;

// Attesa prima di spedire il buffer. Abbastanza da raggruppare una digitazione
// continua, abbastanza poco da non far sembrare il dato a rischio.
const RITARDO_FLUSH = 800;

let timer_flush = null;

/* ------------------------------------------------------------------ */
/* Helper                                                              */
/* ------------------------------------------------------------------ */

function chiave_cella(m) {
  return [
    m.id_prenotazione,
    m.id_consegna || "",
    m.id_prodotto,
    m.id_voce,
  ].join("|");
}

/**
 * Id della voce prenotata, qualunque sia il modello che l'ha scritta.
 *
 * `id_varieta` è il modello corrente; `id_colore` lo storico scritto per
 * colore; `id` la convenzione divergente delle consegne. Il campo presente
 * dice anche di che natura è il riferimento.
 */
function id_di(voce) {
  return String(voce.id_varieta || voce.id_colore || voce.id);
}

/** Nome del campo sotto cui va scritto il riferimento. */
function campo_di(tipo) {
  return tipo === "colore" ? "id_colore" : "id_varieta";
}

function trova_contenitore(prenotazione, id_consegna) {
  if (!id_consegna) return prenotazione;
  return (prenotazione.consegne || []).find((c) => c.id == id_consegna) || null;
}

/**
 * Applica una modifica alla copia locale, rispettando la reattività di Vue 2:
 * gli oggetti colore vengono sostituiti, mai mutati campo per campo.
 */
function applica_locale(prenotazione, m) {
  const contenitore = trova_contenitore(prenotazione, m.id_consegna);
  if (!contenitore) return;
  if (!contenitore.prodotti) contenitore.prodotti = [];

  let prodotto = contenitore.prodotti.find(
    (p) => p.id_prodotto == m.id_prodotto
  );
  if (!prodotto) {
    if (m.qta != null && Number(m.qta) <= 0 && m.nota == null) return;
    prodotto = { id_prodotto: m.id_prodotto, colori: [] };
    contenitore.prodotti.push(prodotto);
  }

  const indice = prodotto.colori.findIndex((c) => id_di(c) == m.id_voce);

  if (indice === -1) {
    if (m.nota == null && (m.qta == null || Number(m.qta) <= 0)) return;
    const nuovo = { [campo_di(m.tipo)]: m.id_voce };
    if (m.qta != null) nuovo.qta = Number(m.qta);
    if (m.nota != null) nuovo.nota = m.nota;
    prodotto.colori.push(nuovo);
    return;
  }

  if (m.qta != null && Number(m.qta) <= 0) {
    prodotto.colori.splice(indice, 1);
    if (!prodotto.colori.length) {
      const i = contenitore.prodotti.indexOf(prodotto);
      if (i !== -1) contenitore.prodotti.splice(i, 1);
    }
    return;
  }

  const aggiornato = { ...prodotto.colori[indice] };
  if (m.qta != null) aggiornato.qta = Number(m.qta);
  if (m.nota != null) aggiornato.nota = m.nota;
  prodotto.colori.splice(indice, 1, aggiornato);
}

function somma_colori(prodotti, id_prodotto, id_voce) {
  const prodotto = (prodotti || []).find((p) => p.id_prodotto == id_prodotto);
  if (!prodotto) return 0;
  const voce = (prodotto.colori || []).find((c) => id_di(c) == id_voce);
  return voce && voce.qta ? Number(voce.qta) : 0;
}

/* ------------------------------------------------------------------ */

export default {
  namespaced: true,

  state: {
    prenotazioni: [],
    dispo: [],
    anno: null,
    // chiave cella -> modifica in attesa di essere spedita
    pendenti: {},
    // id prenotazione -> nota in attesa di essere spedita
    note_pendenti: {},
    // "id_prodotto|id_voce" -> quantità prodotta in attesa
    dispo_pendenti: {},
    anno_dispo: null,
    salvataggio_in_corso: false,
    errore_salvataggio: false,
  },

  getters: {
    tutte: (state) => state.prenotazioni,

    by_id: (state) => (id) =>
      state.prenotazioni.find((p) => p.id == id) || null,

    by_cliente: (state) => (iva) =>
      state.prenotazioni.find((p) => p.cliente == iva) || null,

    /** Quantità di una cella. Senza id_consegna legge il totale prenotato. */
    qta: (state) => ({ id_prenotazione, id_consegna, id_prodotto, id_voce }) => {
      const pren = state.prenotazioni.find((p) => p.id == id_prenotazione);
      if (!pren) return 0;
      const contenitore = trova_contenitore(pren, id_consegna);
      if (!contenitore) return 0;
      return somma_colori(contenitore.prodotti, id_prodotto, String(id_voce));
    },

    /** Prenotato meno quanto già distribuito sulle consegne. */
    rimanenza: (state) => ({ id_prenotazione, id_prodotto, id_voce }) => {
      const pren = state.prenotazioni.find((p) => p.id == id_prenotazione);
      if (!pren) return 0;
      const id_col = String(id_voce);
      const prenotato = somma_colori(pren.prodotti, id_prodotto, id_col);
      const distribuito = (pren.consegne || []).reduce(
        (tot, c) => tot + somma_colori(c.prodotti, id_prodotto, id_col),
        0
      );
      return prenotato - distribuito;
    },

    /** Quantità prodotta. L'anno è quello caricato in `dispo`. */
    qta_dispo: (state) => ({ id_prodotto, id_voce }) => {
      const prodotto = state.dispo.find((d) => d.id_prodotto == id_prodotto);
      if (!prodotto) return 0;
      const voce = (prodotto.colori || []).find(
        (c) => id_di(c) == String(id_voce)
      );
      return voce && voce.qta ? Number(voce.qta) : 0;
    },

    /**
     * Ordinato per prodotto e colore, con i clienti che lo hanno chiesto.
     *
     * Costruita in una passata sola su tutte le prenotazioni caricate e
     * messa in cache da Vuex: la versione precedente rifaceva la scansione
     * dell'intero anno a ogni cella disegnata.
     *
     * Conta anche le prenotazioni evase: la merce consegnata ha comunque
     * consumato produzione.
     */
    mappa_ordinato: (state) => {
      const mappa = {};
      state.prenotazioni.forEach((pren) => {
        (pren.prodotti || []).forEach((prodotto) => {
          const per_colore =
            mappa[prodotto.id_prodotto] || (mappa[prodotto.id_prodotto] = {});
          (prodotto.colori || []).forEach((colore) => {
            const qta = Number(colore.qta || 0);
            if (!qta) return;
            const id = id_di(colore);
            const voce = per_colore[id] || (per_colore[id] = { qta: 0, clienti: [] });
            voce.qta += qta;
            voce.clienti.push({
              iva: pren.cliente,
              qta,
              id_prenotazione: pren.id,
              evasa: !!pren.evasa,
            });
          });
        });
      });
      return mappa;
    },

    /**
     * Quanto resta da promettere di una voce: prodotto meno ordinato da
     * tutti i clienti dell'anno.
     *
     * `null` quando la produzione non è stata impostata: senza quel dato la
     * disponibilità è ignota, non negativa. Mostrare "-90" solo perché
     * nessuno ha ancora inserito la produzione sarebbe un falso allarme, e
     * capiterebbe anche sui prodotti non crisantemo che non hanno dispo.
     */
    disponibile: (state, getters) => ({ id_prodotto, id_voce }) => {
      const prodotta = getters.qta_dispo({ id_prodotto, id_voce });
      if (!prodotta) return null;
      return prodotta - getters.ordinato({ id_prodotto, id_voce }).qta;
    },

    /**
     * Colori ordinati oltre la produzione, dal più scoperto.
     *
     * Non serve risolvere i nomi dei colori per saperlo: bastano le
     * prenotazioni e la disponibilità, quindi il conteggio è disponibile
     * anche senza aprire la pagina.
     */
    scoperti: (state, getters) => {
      const righe = [];
      Object.keys(getters.mappa_ordinato).forEach((id_prodotto) => {
        const per_voce = getters.mappa_ordinato[id_prodotto];
        Object.keys(per_voce).forEach((id_voce) => {
          const ordinata = per_voce[id_voce].qta;
          const prodotta = getters.qta_dispo({ id_prodotto, id_voce });
          const mancante = ordinata - prodotta;
          if (mancante <= 0) return;
          righe.push({ id_prodotto, id_voce, prodotta, ordinata, mancante });
        });
      });
      return righe.sort((a, b) => b.mancante - a.mancante);
    },

    ordinato: (state, getters) => ({ id_prodotto, id_voce }) => {
      const per_voce = getters.mappa_ordinato[id_prodotto];
      const voce = per_voce && per_voce[String(id_voce)];
      return voce || { qta: 0, clienti: [] };
    },

    /** Carrelli CC di una consegna, o dell'intera prenotazione senza id. */
    cc: (state, getters, rootState, rootGetters) => ({ id_prenotazione, id_consegna }) => {
      const pren = state.prenotazioni.find((p) => p.id == id_prenotazione);
      if (!pren) return 0;
      const contenitore = trova_contenitore(pren, id_consegna);
      if (!contenitore) return 0;
      return (contenitore.prodotti || []).reduce((tot, prodotto) => {
        const anagrafica = rootGetters.get_prodotto_by_id(prodotto.id_prodotto);
        // Prodotto uscito dal catalogo, o senza capienza dichiarata:
        // conta zero invece di far esplodere il totale della riga.
        if (!anagrafica || !anagrafica.qta_cc) return tot;
        const pezzi = (prodotto.colori || []).reduce(
          (somma, colore) => somma + Number(colore.qta || 0),
          0
        );
        return tot + pezzi / anagrafica.qta_cc;
      }, 0);
    },

    /** Valore della prenotazione al listino del cliente. */
    valore: (state, getters, rootState, rootGetters) => (id_prenotazione) => {
      const pren = state.prenotazioni.find((p) => p.id == id_prenotazione);
      if (!pren) return 0;
      const cliente = rootGetters.get_cliente_by_iva(pren.cliente);
      const listino = cliente ? cliente.listino : "1";
      return (pren.prodotti || []).reduce((tot, prodotto) => {
        const anagrafica = rootGetters.get_prodotto_by_id(prodotto.id_prodotto);
        if (!anagrafica) return tot;
        const prezzo = parseFloat(
          anagrafica["prezzo" + listino] || anagrafica.prezzo1 || 0
        );
        if (isNaN(prezzo)) return tot;
        const pezzi = (prodotto.colori || []).reduce(
          (somma, colore) => somma + Number(colore.qta || 0),
          0
        );
        return tot + pezzi * prezzo;
      }, 0);
    },

    /**
     * Quanto del prenotato è già assegnato alle consegne.
     *
     * È la rimanenza della griglia vista dall'alto: in lista dice se una
     * prenotazione è pronta a partire o se c'è ancora roba da assegnare,
     * cosa che il solo numero di consegne non racconta.
     */
    ripartizione: (state) => (id_prenotazione) => {
      const vuoto = { prenotato: 0, distribuito: 0, mancante: 0, percentuale: 0 };
      const pren = state.prenotazioni.find((p) => p.id == id_prenotazione);
      if (!pren) return vuoto;

      const pezzi = (contenitore) =>
        (contenitore.prodotti || []).reduce(
          (tot, prodotto) =>
            tot +
            (prodotto.colori || []).reduce(
              (somma, colore) => somma + Number(colore.qta || 0),
              0
            ),
          0
        );

      const prenotato = pezzi(pren);
      const distribuito = (pren.consegne || []).reduce(
        (tot, consegna) => tot + pezzi(consegna),
        0
      );

      return {
        prenotato,
        distribuito,
        mancante: prenotato - distribuito,
        percentuale: prenotato
          ? Math.min(100, Math.round((distribuito / prenotato) * 100))
          : 0,
      };
    },

    modifiche_pendenti: (state) =>
      Object.keys(state.pendenti).length +
      Object.keys(state.note_pendenti).length +
      Object.keys(state.dispo_pendenti).length,
    salvataggio_in_corso: (state) => state.salvataggio_in_corso,
    errore_salvataggio: (state) => state.errore_salvataggio,
  },

  mutations: {
    set_prenotazioni(state, lista) {
      state.prenotazioni = lista;
    },

    set_anno(state, anno) {
      state.anno = anno;
    },

    set_prenotazione(state, prenotazione) {
      const i = state.prenotazioni.findIndex((p) => p.id == prenotazione.id);
      if (i === -1) {
        state.prenotazioni.push(prenotazione);
      } else {
        state.prenotazioni.splice(i, 1, prenotazione);
      }
    },

    rimuovi_prenotazione(state, id) {
      const i = state.prenotazioni.findIndex((p) => p.id == id);
      if (i !== -1) state.prenotazioni.splice(i, 1);
    },

    set_dispo(state, lista) {
      state.dispo = lista;
    },

    set_anno_dispo(state, anno) {
      state.anno_dispo = anno;
    },

    applica_dispo(state, { id_prodotto, id_voce, tipo, qta }) {
      let prodotto = state.dispo.find((d) => d.id_prodotto == id_prodotto);
      if (!prodotto) {
        if (Number(qta) <= 0) return;
        prodotto = { id_prodotto, anno: state.anno_dispo, colori: [] };
        state.dispo.push(prodotto);
      }
      const indice = prodotto.colori.findIndex(
        (c) => id_di(c) == String(id_voce)
      );
      if (Number(qta) <= 0) {
        if (indice !== -1) prodotto.colori.splice(indice, 1);
        return;
      }
      if (indice === -1) {
        prodotto.colori.push({ [campo_di(tipo)]: String(id_voce), qta: Number(qta) });
      } else {
        // Sostituzione dell'oggetto per la reattività su chiavi nuove.
        prodotto.colori.splice(indice, 1, {
          ...prodotto.colori[indice],
          qta: Number(qta),
        });
      }
    },

    accoda_dispo(state, modifica) {
      const k = `${modifica.id_prodotto}|${modifica.id_voce}`;
      state.dispo_pendenti = { ...state.dispo_pendenti, [k]: modifica };
    },

    scarica_dispo(state, chiavi) {
      const rimaste = { ...state.dispo_pendenti };
      chiavi.forEach((k) => delete rimaste[k]);
      state.dispo_pendenti = rimaste;
    },

    riaccoda_dispo(state, modifiche) {
      const aggiornate = { ...state.dispo_pendenti };
      modifiche.forEach((m) => {
        const k = `${m.id_prodotto}|${m.id_voce}`;
        if (!(k in aggiornate)) aggiornate[k] = m;
      });
      state.dispo_pendenti = aggiornate;
    },

    applica_colore(state, modifica) {
      const pren = state.prenotazioni.find(
        (p) => p.id == modifica.id_prenotazione
      );
      if (pren) applica_locale(pren, modifica);
    },

    accoda(state, modifica) {
      const k = chiave_cella(modifica);
      // Fuso, non sostituito: quantità e nota sono campi diversi della stessa
      // cella, e sostituire farebbe sparire quello impostato per primo.
      // Spread anche sull'oggetto esterno, per la reattività su chiavi nuove.
      state.pendenti = {
        ...state.pendenti,
        [k]: { ...state.pendenti[k], ...modifica },
      };
    },

    scarica_pendenti(state, chiavi) {
      const rimaste = { ...state.pendenti };
      chiavi.forEach((k) => delete rimaste[k]);
      state.pendenti = rimaste;
    },

    riaccoda(state, modifiche) {
      const aggiornate = { ...state.pendenti };
      modifiche.forEach((m) => {
        // Campo per campo vince quello arrivato nel frattempo, che è più
        // recente; i campi che nessuno ha ritoccato tornano in coda.
        const k = chiave_cella(m);
        aggiornate[k] = { ...m, ...aggiornate[k] };
      });
      state.pendenti = aggiornate;
    },

    applica_nota(state, { id_prenotazione, nota }) {
      const pren = state.prenotazioni.find((p) => p.id == id_prenotazione);
      // `nota` è sempre presente nella serializzazione: assegnarla è reattivo.
      if (pren) pren.nota = nota;
    },

    accoda_nota(state, { id_prenotazione, nota }) {
      state.note_pendenti = { ...state.note_pendenti, [id_prenotazione]: nota };
    },

    scarica_note(state, ids) {
      const rimaste = { ...state.note_pendenti };
      ids.forEach((id) => delete rimaste[id]);
      state.note_pendenti = rimaste;
    },

    riaccoda_note(state, note) {
      const aggiornate = { ...state.note_pendenti };
      Object.keys(note).forEach((id) => {
        // Se nel frattempo è stata riscritta, vince la versione più recente.
        if (!(id in aggiornate)) aggiornate[id] = note[id];
      });
      state.note_pendenti = aggiornate;
    },

    set_ordine_consegna(state, { id_prenotazione, id_consegna, id_ordine }) {
      const pren = state.prenotazioni.find((p) => p.id == id_prenotazione);
      if (!pren) return;
      const i = (pren.consegne || []).findIndex((c) => c.id == id_consegna);
      if (i === -1) return;
      pren.consegne.splice(i, 1, {
        ...pren.consegne[i],
        id_ordine_creato: id_ordine,
      });
    },

    set_salvataggio(state, in_corso) {
      state.salvataggio_in_corso = in_corso;
    },

    set_errore_salvataggio(state, errore) {
      state.errore_salvataggio = errore;
    },
  },

  actions: {
    async fetch_prenotazioni({ commit, rootGetters }, anno) {
      const { data } = await axios.get(
        `${BASE}/admin/crisantemi/prenotazioni/anno/${anno}`,
        { auth: rootGetters.get_credential }
      );
      commit("set_anno", String(anno));
      commit("set_prenotazioni", data);
    },

    async fetch_dispo({ commit, rootGetters }, anno) {
      const { data } = await axios.get(
        `${BASE}/admin/crisantemi/dispo/anno/${anno}`,
        { auth: rootGetters.get_credential }
      );
      commit("set_anno_dispo", String(anno));
      commit("set_dispo", data);
    },

    /** Quantità prodotta di un colore. Stesso buffer di tutto il resto. */
    imposta_dispo({ commit, dispatch }, { id_prodotto, id_voce, tipo, qta }) {
      const modifica = {
        id_prodotto,
        id_voce: String(id_voce),
        tipo,
        qta: Number(qta),
      };
      commit("applica_dispo", modifica);
      commit("accoda_dispo", modifica);
      dispatch("programma_flush");
    },

    /**
     * Cerca la prenotazione di un cliente per un anno qualsiasi, anche
     * diverso da quello attualmente in lista.
     */
    async cerca_prenotazione({ rootGetters }, { cliente, anno }) {
      const { data } = await axios.get(
        `${BASE}/admin/crisantemi/prenotazione/anno/${anno}/cliente/${cliente}`,
        { auth: rootGetters.get_credential }
      );
      return data && data.length ? data[0] : null;
    },

    /**
     * Crea una prenotazione vuota.
     * Se esiste già restituisce quella esistente con `gia_esistente: true`
     * invece di sollevare: al chiamante serve per proporre di aprirla.
     */
    async crea_prenotazione({ commit, rootGetters }, { cliente, anno }) {
      try {
        const { data } = await axios.post(
          `${BASE}/admin/crisantemi/prenotazioni`,
          { cliente, anno: String(anno) },
          { auth: rootGetters.get_credential }
        );
        commit("set_prenotazione", data);
        return data;
      } catch (err) {
        const esistente =
          err.response &&
          err.response.status === 409 &&
          err.response.data &&
          err.response.data.prenotazione;
        if (!esistente) throw err;
        commit("set_prenotazione", esistente);
        return { ...esistente, gia_esistente: true };
      }
    },

    /**
     * Copia prodotti e colori da un altro anno.
     * Restituisce null se nell'anno di origine non c'è nulla da copiare.
     */
    async clona_prenotazione({ commit, rootGetters }, { cliente, da_anno, a_anno }) {
      const { data } = await axios.put(
        `${BASE}/admin/crisantemi/clona_prenotazione/${cliente}/da_anno/${da_anno}/a_anno/${a_anno}`,
        {},
        { auth: rootGetters.get_credential }
      );
      if (data.res !== "ok") return null;
      if (data.prenotazione) commit("set_prenotazione", data.prenotazione);
      return data.prenotazione || null;
    },

    async imposta_data_presunta({ commit, rootGetters }, { id, ts }) {
      const { data } = await axios.put(
        `${BASE}/admin/crisantemi/prenotazione/${id}/consegna_presunta/${ts}`,
        {},
        { auth: rootGetters.get_credential }
      );
      commit("set_prenotazione", data);
      return data;
    },

    /**
     * Registra la modifica di una cella: applica subito in locale, la mette in
     * coda e programma l'invio. Non attende la rete.
     *
     * @param {Object} m  { id_prenotazione, id_prodotto, id_colore,
     *                      qta?, nota?, id_consegna? }
     */
    imposta_voce({ commit, dispatch }, m) {
      commit("applica_colore", m);
      commit("accoda", m);
      dispatch("programma_flush");
    },

    /**
     * Nota generale della prenotazione. Passa dallo stesso buffer delle celle,
     * così l'indicatore di salvataggio la copre e non esiste un secondo
     * percorso da ricordarsi di svuotare prima di stampare o creare l'ordine.
     */
    imposta_nota_prenotazione({ commit, dispatch }, { id_prenotazione, nota }) {
      commit("applica_nota", { id_prenotazione, nota });
      commit("accoda_nota", { id_prenotazione, nota });
      dispatch("programma_flush");
    },

    programma_flush({ commit, dispatch }) {
      commit("set_errore_salvataggio", false);
      if (timer_flush) clearTimeout(timer_flush);
      timer_flush = setTimeout(() => {
        timer_flush = null;
        dispatch("flush");
      }, RITARDO_FLUSH);
    },

    /**
     * Spedisce il buffer. Da attendere prima di ogni operazione che legge la
     * prenotazione lato server (creazione ordine, stampa, riempi rimanenza),
     * altrimenti quella lavorerebbe su un dato più vecchio di quello a video.
     */
    async flush({ state, commit, rootGetters }) {
      if (timer_flush) {
        clearTimeout(timer_flush);
        timer_flush = null;
      }

      const chiavi = Object.keys(state.pendenti);
      const id_note = Object.keys(state.note_pendenti);
      const chiavi_dispo = Object.keys(state.dispo_pendenti);
      if (!chiavi.length && !id_note.length && !chiavi_dispo.length) return;

      const dispo_inviata = chiavi_dispo.map((k) => state.dispo_pendenti[k]);
      commit("scarica_dispo", chiavi_dispo);

      const da_inviare = chiavi.map((k) => state.pendenti[k]);
      const note_inviate = {};
      id_note.forEach((id) => {
        note_inviate[id] = state.note_pendenti[id];
      });

      commit("scarica_pendenti", chiavi);
      commit("scarica_note", id_note);
      commit("set_salvataggio", true);

      // Normalmente è una sola prenotazione, ma la griglia della
      // disponibilità può toccarne diverse.
      const per_prenotazione = {};
      da_inviare.forEach((m) => {
        if (!per_prenotazione[m.id_prenotazione]) {
          per_prenotazione[m.id_prenotazione] = [];
        }
        per_prenotazione[m.id_prenotazione].push(m);
      });
      // Una nota senza celle toccate vale comunque una richiesta.
      id_note.forEach((id) => {
        if (!per_prenotazione[id]) per_prenotazione[id] = [];
      });

      // La disponibilità viaggia su un endpoint suo, ma nello stesso giro:
      // l'indicatore di salvataggio resta uno solo per tutto il modulo.
      const invio_dispo = dispo_inviata.length
        ? axios
            .put(
              `${BASE}/admin/crisantemi/dispo/anno/${state.anno_dispo}`,
              {
                modifiche: dispo_inviata.map((m) => ({
                  id_prodotto: m.id_prodotto,
                  [campo_di(m.tipo)]: m.id_voce,
                  qta: m.qta,
                })),
              },
              { auth: rootGetters.get_credential }
            )
            .then(({ data }) => {
              commit("set_dispo", data.dispo);
              if (data.scartate && data.scartate.length) {
                commit("set_errore_salvataggio", true);
              }
              // Riapplica quanto toccato mentre la richiesta era in volo.
              Object.keys(state.dispo_pendenti).forEach((k) =>
                commit("applica_dispo", state.dispo_pendenti[k])
              );
            })
        : Promise.resolve();

      try {
        await Promise.all([
          invio_dispo,
          ...Object.keys(per_prenotazione).map(async (id_prenotazione) => {
            const modifiche = per_prenotazione[id_prenotazione].map((m) => ({
              id_prodotto: m.id_prodotto,
              [campo_di(m.tipo)]: m.id_voce,
              ...(m.qta != null ? { qta: Number(m.qta) } : {}),
              ...(m.nota != null ? { nota: m.nota } : {}),
              ...(m.id_consegna ? { id_consegna: m.id_consegna } : {}),
            }));

            const corpo = { modifiche };
            if (id_prenotazione in note_inviate) {
              corpo.nota = note_inviate[id_prenotazione];
            }

            const { data } = await axios.put(
              `${BASE}/admin/crisantemi/prenotazione/${id_prenotazione}/colori`,
              corpo,
              { auth: rootGetters.get_credential }
            );

            commit("set_prenotazione", data);

            // Il server applica quello che può e elenca il resto. Una cella
            // scartata non deve passare inosservata: lo stato locale mostra
            // un valore che sul server non esiste.
            if (data.scartate && data.scartate.length) {
              commit("set_errore_salvataggio", true);
            }

            // Il server ha sovrascritto lo stato: le celle toccate mentre la
            // richiesta era in volo vanno riapplicate sopra la risposta.
            Object.keys(state.pendenti)
              .map((k) => state.pendenti[k])
              .filter((m) => m.id_prenotazione == id_prenotazione)
              .forEach((m) => commit("applica_colore", m));

            if (id_prenotazione in state.note_pendenti) {
              commit("applica_nota", {
                id_prenotazione,
                nota: state.note_pendenti[id_prenotazione],
              });
            }
          }),
        ]);
      } catch (err) {
        // Rimettiamo in coda: lo stato locale mostra già i valori voluti,
        // il prossimo flush riproverà a inviarli.
        commit("riaccoda", da_inviare);
        commit("riaccoda_note", note_inviate);
        commit("riaccoda_dispo", dispo_inviata);
        commit("set_errore_salvataggio", true);
        throw err;
      } finally {
        commit("set_salvataggio", false);
      }
    },

    async cambia_ritiro({ commit, rootGetters }, { id, ritiro }) {
      const { data } = await axios.put(
        `${BASE}/admin/crisantemi/prenotazione/${id}/ritiro/${ritiro ? 1 : 0}`,
        {},
        { auth: rootGetters.get_credential }
      );
      commit("set_prenotazione", data);
    },

    async cambia_evasione({ commit, rootGetters }, { id, evasa }) {
      const { data } = await axios.put(
        `${BASE}/admin/crisantemi/prenotazione/${id}/evasa/${evasa}`,
        {},
        { auth: rootGetters.get_credential }
      );
      commit("set_prenotazione", data);
    },

    /* --- Consegne ---------------------------------------------------- */

    async crea_consegna({ commit, rootGetters }, id_prenotazione) {
      const { data } = await axios.post(
        `${BASE}/admin/crisantemi/crea_consegna/${id_prenotazione}`,
        {},
        { auth: rootGetters.get_credential }
      );
      commit("set_prenotazione", data);
      return data;
    },

    async elimina_consegna({ dispatch, commit, rootGetters }, { id_prenotazione, id_consegna }) {
      // Prima le celle in sospeso, altrimenti finirebbero su una consegna
      // che sul server non esiste più e verrebbero scartate.
      await dispatch("flush");
      const { data } = await axios.delete(
        `${BASE}/admin/crisantemi/prenotazione/${id_prenotazione}/consegna/${id_consegna}`,
        { auth: rootGetters.get_credential }
      );
      commit("set_prenotazione", data);
    },

    async cambia_data_consegna({ commit, rootGetters }, { id_prenotazione, id_consegna, ts }) {
      const { data } = await axios.put(
        `${BASE}/admin/crisantemi/prenotazione/${id_prenotazione}/consegna/${id_consegna}/data/${ts}`,
        {},
        { auth: rootGetters.get_credential }
      );
      commit("set_prenotazione", data);
    },

    async riempi_rimanenza({ dispatch, commit, rootGetters }, { id_prenotazione, id_consegna }) {
      // Il server calcola la rimanenza leggendo la prenotazione: deve vedere
      // le quantità appena digitate.
      await dispatch("flush");
      const { data } = await axios.post(
        `${BASE}/admin/crisantemi/prenotazione/${id_prenotazione}/consegna/${id_consegna}/riempi_rimanenza`,
        {},
        { auth: rootGetters.get_credential }
      );
      commit("set_prenotazione", data);
    },

    async crea_ordine_da_consegna({ dispatch, commit, rootGetters }, { id_prenotazione, id_consegna }) {
      // L'ordine è costruito dai colori della consegna lato server.
      await dispatch("flush");
      const { data } = await axios.post(
        `${BASE}/admin/crisantemi/prenotazione/${id_prenotazione}/consegna/${id_consegna}/crea_ordine`,
        {},
        { auth: rootGetters.get_credential }
      );
      commit("add_new_order", data, { root: true });
      commit("set_ordine_consegna", {
        id_prenotazione,
        id_consegna,
        id_ordine: data._id.$oid,
      });
      return data;
    },

    async elimina_prenotazione({ commit, rootGetters }, id) {
      await axios.delete(`${BASE}/admin/crisantemi/prenotazione/${id}`, {
        auth: rootGetters.get_credential,
      });
      commit("rimuovi_prenotazione", id);
    },

    SOCKET_AGGIORNA_PRENOTAZIONE_CRIS({ state, commit }, prenotazione) {
      commit("set_prenotazione", prenotazione);
      // Le nostre modifiche non ancora spedite restano valide: le rimettiamo
      // sopra la versione arrivata dal server.
      Object.keys(state.pendenti)
        .map((k) => state.pendenti[k])
        .filter((m) => m.id_prenotazione == prenotazione.id)
        .forEach((m) => commit("applica_colore", m));
    },
  },
};
